import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useApiCall } from 'hooks/useApiCall';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DropdownWithDeletedValueTemplate } from 'utils/DropdownWithDeletedValueTemplate';

import { MeasureUnitService } from 'components/services/MeasureUnitService';

import { ObjectForSelectionTemplate } from '../constants/ObjectForSelectionTemplate';
import { ObjectParameterSchema } from '../constants/schemas';
import useAssetParametersService from '../hooks/services/useAssetParametersService';
import { AssetParameter, MeasureUnitUI, TagType } from '../types/interfaces';
import { ObjectForSelection } from '../types/interfaces/ObjectForSelection';
import { ObjectCharacteristicProps } from '../types/props';

import '../ui/css/EmployeeCard.css';
import './css/ObjectParameterCard.css';

import { CharacteristicValueComponent } from './CharacteristicValueComponent';
import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';


interface RefInterface {
  handleCancel: () => void;
}

const ObjectParameterCard = React.forwardRef<
  RefInterface,
  ObjectCharacteristicProps
>(
  (
    {
      objectCharacteristicProps,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog
    }: ObjectCharacteristicProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      setError,
      formState: { errors, isDirty }
    } = useForm({
      resolver: zodResolver(ObjectParameterSchema),
      defaultValues: objectCharacteristicProps
    });
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedTypeFilter, setSelectedTypeFilter] = useState<TagType[]>([]);
    const [measures, setMeasures] = useState<MeasureUnitUI[]>([]);
    const [, forceUpdate] = useState<number>(0);
    const toast = useRef<Toast>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const name = watch('name');
    const measure = watch('measure');
    const values = watch('values');
    const objectCharacteristicId = watch('id');

    const assetParametersService = useAssetParametersService();
    // useEffect(() => {
    //   const serviceResult = assetParametersService.getWithMeasures(
    //     objectCharacteristicProps.id
    //       ? parseInt(objectCharacteristicProps.id.toString())
    //       : undefined
    //   );

    //   serviceResult.result.then((r) => {
    //     if (r.assetParameter) reset(r.assetParameter);
    //     if (r.measures) setMeasures(r.measures);
    //   });
    //   return () => {
    //     if (serviceResult) serviceResult.cancel();
    //   };
    // }, [assetParametersService, objectCharacteristicProps.id, reset]);

    const getMeasures = useCallback(async () => {
      const measures = await MeasureUnitService.getMeasureUnitForSelection(); 
      setMeasures(measures.data.value);
    }, []);
    useApiCall(getMeasures);


    useEffect(() => {
      setSelectedTypeFilter(values);
      if (scrollContainerRef.current && values && values.length > 0) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, [values]);

    const onSubmit: SubmitHandler<AssetParameter> = async (data) => {
      const newData = { ...data };
      if (newData.measure && newData.measure.label === '')
        newData.measure = undefined;

      newData.values = newData.values.map((x) => ({
        ...x,
        id: x.id < 0 ? 0 : x.id
      }));
      let result: number | undefined = 0;
      const dataId = data.id;
      const isIdExist = data && dataId !== undefined && dataId !== null;

      if (onUpdate && isIdExist) {
        result = await onUpdate(dataId, newData);
      }
      if (onAdd && !isIdExist) {
        newData.id = 0;
        result = await onAdd(newData);
      }
      if (result === -1) {
        setError('name', {
          type: 'value',
          message:
            'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название'
        });
      } else {
        closeDialog && closeDialog();
      }
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      }
      if (closeDialog && !isDirty) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
      // Perform navigation/close the card without saving
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(objectCharacteristicId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleDeleteCharacteristic = (
      characteristicId: string | number | undefined
    ) => {
      const index = selectedTypeFilter.findIndex(
        (tagComponent) => tagComponent.id === characteristicId
      );
      if (index !== -1) {
        const updatedTaskStatuses = [
          ...selectedTypeFilter.slice(0, index),
          ...selectedTypeFilter.slice(index + 1)
        ];
        setSelectedTypeFilter(updatedTaskStatuses);
        setValue(
          'values',
          updatedTaskStatuses.map((x) => ({
            ...x,
            id: parseInt(x.id?.toString() ?? '0')
          }))
        );
        setSelectedTypeFilter(updatedTaskStatuses);
      }
    };

    const handleAddTaskStatus = () => {
      // Add a new empty task status object to the form data
      const currentTaskStatuses = values || [];
      const newTaskStatus = ObjectForSelectionTemplate;
      newTaskStatus.id = -Date.now();
      setValue('values', [...currentTaskStatuses, newTaskStatus], {
        shouldDirty: true
      });
    };

    const handleInputChange = (field: string, value: string) => {
      //@ts-ignore
      setValue(field, value);

      // If the changed field is 'taskStatuses.actionName', update the state
      if (field.startsWith('taskStatuses') && field.endsWith('.actionName')) {
        // Extract the index from the field
        const indexMatch = field.match(/\d+/);
        const index = indexMatch ? parseInt(indexMatch[0]) : -1;

        // Update the state based on the index
        if (index !== -1) {
          setValue(`values.${index}.label`, value);
        }
      }
    };

    return (
      <div className="dialog-tabless-card asset-characteristic-card asset-characteristic-card__roles">
        <Toast ref={toast} />
        <div className=" gap-3 pb-0">
          <Divider />
          <div className="objectcharacteristic-card__column">
            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex-row justify-content-between align-items-center w-full">
                  <label htmlFor="name" className="p-0">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="p-0">
                    <InputText
                      className="asset-characteristic-card__input"
                      {...register('name')}
                    />
                  </div>
                </div>
                <p
                  className={`asset-characteristic-card-errors card-errors ${
                    errors.name ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.name ? errors.name.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="measure" className="p-0">
                    Единица изменения:
                  </label>
                  <div className="p-0 asset-characteristic-card__right-item">
                    <Dropdown
                      filter
                      showClear
                      value={measure}
                      onChange={(e: DropdownChangeEvent) => {
                        const selectedValue = e.value;
                        forceUpdate((prev) => prev + 1);

                        setValue('measure', selectedValue, {
                          shouldDirty: true
                        });
                      }}
                      options={measures}
                      placeholder={measure?.label ?? ''}
                      className="w-full"
                      width="100%"
                      style={{height: 42}}
                      valueTemplate={(option) => (
                        <DropdownWithDeletedValueTemplate 
                          option={option} 
                          selectedItem={measure}
                          placeholder={measure?.label ?? ''}
                        />
                      )}
                    />
                  </div>
                </div>
                <p
                  className={`asset-characteristic-card-errors card-errors ${
                    errors.measure ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.measure ? (errors.measure as any).message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className={`flex flex-row justify-content-between ${values?.length === 0 ? 'align-items-center' : ''} w-full`}>
                  <label htmlFor="values" className="p-0" style={values?.length === 0 ? undefined : {marginTop: 12}}>
                    Ввод значений:
                  </label>
                  <div className="asset-characteristic-card__input">
                    <div
                      ref={scrollContainerRef}
                      className="scrollable-container asset-characteristic-card__right-item"
                      style={{
                        width: '100%',
                        maxHeight: '391px',
                        overflow: 'auto',
                        padding: '0 4px 4px 0'
                      }}
                      id="tags-scroll"
                    >
                      {values &&
                        values.map((characteristicValue, index) => (
                          <CharacteristicValueComponent
                            errors={errors?.values?.[index]}
                            passedClassName={
                              index !== 0 ? 'dialog-card__row' : ''
                            }
                            key={characteristicValue.id}
                            characteristic={characteristicValue}
                            index={index}
                            onInputChange={(value, property) =>
                              handleInputChange(
                                `taskStatuses.${index}.${property}`,
                                value
                              )
                            }
                            onDelete={() =>
                              handleDeleteCharacteristic(characteristicValue.id)
                            } // Pass the onDelete function
                          />
                        ))}
                      <div
                        className="flex justify-content-end dialog-card__row"
                        style={{ width: '100%' }}
                      >
                        <Button
                          className="align-self-end add-button"
                          icon="pi pi-plus"
                          severity="success"
                          onClick={handleAddTaskStatus}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="характеристику объекта"
          entityName={name}
        />
      </div>
    );
  }
);

ObjectParameterCard.displayName = 'ObjectParameterCard';

export default ObjectParameterCard;
